import * as React from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@ooh/ui/components/popover';
import { INTERACTIVE_CELL_CLASSNAME } from '@ooh/ui/components/data-table';
import {
  AvailabilityCellVisual,
  extractShortOwnerLabel,
  POPOVER_CONTENT_CLAMP_CLASS,
  POPOVER_CONTENT_WIDTH_CLASS,
  type CellState,
} from '../../../availability';

// Re-exported for `__tests__/availability-rich-cell.test.tsx`, which imports this
// alongside `RichAvailabilityCell` from this file — the canonical implementation now
// lives in `availability-cell-helpers.tsx` (shared with `AvailabilityCellVisual`).
export { extractShortOwnerLabel };

/**
 * Single source of truth for this cell's `data-testid` prefix — every caller that
 * resolves a cell's DOM node by testid (e.g. `useMultiSelectAnchor`'s anchor
 * resolution) MUST use this constant instead of retyping the literal, so a rename
 * here can never silently reintroduce the anchor-resolution regression fixed in
 * CU-86cbejhgd (a caller passing a stale/mismatched prefix that never matches this
 * cell's real testid, leaving the multi-select popover permanently un-anchored).
 */
export const RICH_AVAILABILITY_CELL_TESTID_PREFIX = 'rich-availability-cell';

/**
 * Builds the click/keydown/mouse handlers for the cell's trigger div — extracted so
 * `RichAvailabilityCell`'s own complexity stays under budget. In select mode, clicks
 * report to the caller's shared-selection callback instead of toggling this cell's own
 * Popover.
 *
 * The drag mouse handlers (`onMouseDown`/`onMouseEnter`/`onMouseUp`) are wired whenever
 * the caller supplies them, INDEPENDENTLY of `selectMode` (task 4.1,
 * `reservation-mixed-range-and-target-expiry`): a cell can be MULTI-SELECT-ELIGIBLE (so a
 * drag rectangle passing through or ending on it must keep tracking the drag) while still
 * being outside the narrower SELECT-eligible set that drives `selectMode` — e.g. a
 * `reserved-other` cell, which must keep opening its own INSPECT popover on a lone click.
 * Attaching the drag handlers there is harmless: a plain click (mousedown+mouseup with no
 * intervening drag) never sets the caller's `isDraggingRef`, so the cell's own Popover
 * still opens normally via `PopoverTrigger`'s composed `onClick`.
 */
function buildCellInteractionHandlers({
  selectMode,
  onSelectClick,
  onSelectMouseDown,
  onSelectMouseEnter,
  onSelectMouseUp,
  isOpen,
  onOpenChange,
}: {
  selectMode: boolean;
  onSelectClick?: (shiftKey: boolean) => void;
  onSelectMouseDown?: () => void;
  onSelectMouseEnter?: () => void;
  onSelectMouseUp?: () => void;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return {
    onClick: (e: React.MouseEvent) => {
      e.stopPropagation();
      if (selectMode) onSelectClick?.(e.shiftKey);
    },
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      e.preventDefault();
      e.stopPropagation();
      if (selectMode) {
        onSelectClick?.(false);
      } else {
        onOpenChange(!isOpen);
      }
    },
    // preventDefault stops the browser from starting a native text-selection drag at
    // this mousedown — without it, dragging a selection rectangle across cells can spill
    // into and highlight text in unrelated columns on the same row (CU-86cbejhgd). Gated
    // on handler PRESENCE, not `selectMode`: task 4.1
    // (`reservation-mixed-range-and-target-expiry`) deliberately decoupled these drag
    // handlers from `selectMode` so a `reserved-other`/`option-other` cell can participate
    // in drag geometry while still opening its own INSPECT popover on a lone click —
    // gating preventDefault on `selectMode` here would let the exact same text-selection
    // spill come straight back on precisely those newly-draggable cells.
    onMouseDown: onSelectMouseDown
      ? (e: React.MouseEvent) => {
          e.preventDefault();
          onSelectMouseDown?.();
        }
      : undefined,
    onMouseEnter: onSelectMouseEnter ? () => onSelectMouseEnter?.() : undefined,
    onMouseUp: onSelectMouseUp ? () => onSelectMouseUp?.() : undefined,
  };
}

/**
 * Builds the cell trigger's highlight class name — extracted so `RichAvailabilityCell`'s
 * own complexity stays under budget.
 *
 * Includes `INTERACTIVE_CELL_CLASSNAME` (`relative z-[1]`) so this trigger always wins
 * click/drag hit-tests over the inventory catalog's row-link stretched-link overlay (see
 * apps/web/src/routes/media-units/index.tsx's DataTable `renderRowLink`) — without it, a
 * click on this cell would fall through to the overlay and navigate to the unit detail
 * page instead of opening the popover / participating in select mode.
 */
function buildTriggerClassName(highlight: boolean): string {
  const base = `w-full h-full flex items-center justify-center gap-1 px-1 overflow-hidden ${INTERACTIVE_CELL_CLASSNAME}`;
  return highlight ? `${base} ring-2 ring-inset ring-primary bg-primary/10` : base;
}

export interface RichAvailabilityCellProps {
  state: CellState;
  ownerHint?: string;
  /**
   * The dominant entry's raw `offerTitle` — forwarded to `AvailabilityCellVisual` to
   * scope its inline short-label extraction. See `AvailabilityCellVisualProps.offerTitle`.
   */
  offerTitle?: string;
  /**
   * True when the occupying entry backing `ownerHint` is a conditional sale
   * (HARD_RESERVATION with `conditional: true`) — applies the same red-font
   * treatment the old full-availability grid's `MultiStackCellContent` used, so
   * the merged rich cell stays visually equivalent per the "conditional-sale red
   * font" requirement.
   */
  ownerConditional?: boolean;
  /** The dominant entry's expiry deadline, pre-formatted. See `AvailabilityCellVisualProps`. */
  deadlineLabel?: string;
  /** Column pixel width — controls whether the inline owner label fits next to the icon. */
  colWidth: number;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Rendered inside the click popover (e.g. Rezerwacja/Opcja/Zablokuj actions). The caller
   * owns the action buttons so both mockup callers (NotImplementedAction-wrapped, no-op)
   * and the eventual real caller (real reservation mutations) can supply their own without
   * this component depending on either.
   */
  popoverContent: React.ReactNode;
  /** Row id for this cell — combined with `periodId` to keep `data-testid`s unique per cell. */
  rowId: string;
  /** Period id for this cell — combined with `rowId` to keep `data-testid`s unique per cell. */
  periodId: string;
  /**
   * Multi-select mode (task 4.5, `unified-availability-interaction`) — when
   * `onSelectClick` is provided, this cell participates in the shared rectangle-
   * selection state lifted to the route instead of opening its own independent
   * Popover: `isOpen`/`onOpenChange`/`popoverContent` are ignored, and clicking the
   * cell calls `onSelectClick` instead. Omit to leave the cell on the pre-existing
   * per-cell-popover behavior (byte-for-byte unchanged default).
   *
   * `onSelectMouseDown`/`onSelectMouseEnter`/`onSelectMouseUp` are wired INDEPENDENTLY
   * of `onSelectClick` (task 4.1, `reservation-mixed-range-and-target-expiry`): a caller
   * may supply them even when `onSelectClick` is omitted, so a drag rectangle keeps
   * tracking through/onto a cell whose OWN Popover still opens on a lone click (e.g. a
   * `reserved-other` cell, which is multi-select-eligible for rectangle geometry but not
   * select-mode-eligible for a lone click).
   */
  isSelected?: boolean;
  onSelectMouseDown?: () => void;
  onSelectMouseEnter?: () => void;
  onSelectMouseUp?: () => void;
  onSelectClick?: (shiftKey: boolean) => void;
}

/**
 * One appended availability cell for the inventory catalog `DataTable`. Renders an inline
 * owner label when the column is wide enough to fit it, a hover tooltip with the full
 * owner + period info, and a click popover for cell actions.
 *
 * The click handler only calls `stopPropagation` — `PopoverTrigger`'s own composed onClick
 * already toggles the controlled `open` state via `onOpenChange` (Radix reads open/
 * onOpenChange off the parent `<Popover>`). Also calling `onOpenChange` from this handler
 * would double-toggle and cancel out within the same click.
 */
export function RichAvailabilityCell({
  state,
  ownerHint,
  offerTitle,
  ownerConditional = false,
  deadlineLabel,
  colWidth,
  isOpen,
  onOpenChange,
  popoverContent,
  rowId,
  periodId,
  isSelected = false,
  onSelectMouseDown,
  onSelectMouseEnter,
  onSelectMouseUp,
  onSelectClick,
}: RichAvailabilityCellProps) {
  const selectMode = onSelectClick !== undefined;
  const interactionHandlers = buildCellInteractionHandlers({
    selectMode,
    onSelectClick,
    onSelectMouseDown,
    onSelectMouseEnter,
    onSelectMouseUp,
    isOpen,
    onOpenChange,
  });

  // Reflects rectangle-selection membership regardless of `selectMode` (task 4.1) — a
  // multi-select-eligible-but-not-select-mode cell (e.g. `reserved-other`) can still be
  // part of the active selection and must show the same highlight ring.
  const highlight = isSelected;
  // Kept as a plain inline <div> (not a wrapped sub-component) — Radix's PopoverTrigger
  // `asChild` clones props (including the composed onClick that toggles `open`) directly
  // onto its single child element; a custom function-component child would swallow those
  // props instead of forwarding them to the real DOM node.
  const content = (
    <div
      role="button"
      tabIndex={0}
      className={buildTriggerClassName(highlight)}
      data-testid={`${RICH_AVAILABILITY_CELL_TESTID_PREFIX}-${rowId}-${periodId}`}
      data-unified-select-cell
      {...interactionHandlers}
    >
      <AvailabilityCellVisual
        state={state}
        ownerHint={ownerHint}
        offerTitle={offerTitle}
        ownerConditional={ownerConditional}
        deadlineLabel={deadlineLabel}
        colWidth={colWidth}
      />
    </div>
  );

  const trigger = selectMode ? content : <PopoverTrigger asChild>{content}</PopoverTrigger>;

  if (selectMode) {
    return trigger;
  }

  return (
    <Popover open={isOpen} onOpenChange={onOpenChange}>
      {trigger}
      <PopoverContent
        align="start"
        // The queue-join panel (task 4.3, `reservation-mixed-range-and-target-expiry`)
        // added a mandatory shared-expiry date control that renders unconditionally,
        // on top of the identity form and any of the caller's own queue-entry rows —
        // tall enough on some cells that Radix's `avoidCollisions` shifting alone can
        // no longer keep every control inside the viewport (there is nowhere left to
        // shift TO once content height exceeds available height). Cap height to
        // Radix's own measured available space and let the body scroll instead, the
        // same pattern `select.tsx` already uses for `--radix-select-content-available-height`.
        className={`p-1 ${POPOVER_CONTENT_WIDTH_CLASS} ${POPOVER_CONTENT_CLAMP_CLASS}`}
        onClick={(e) => e.stopPropagation()}
        data-testid={`rich-availability-popover-${rowId}-${periodId}`}
      >
        {popoverContent}
      </PopoverContent>
    </Popover>
  );
}
